import { getBlocksData, getPost } from '@zillow/cms-blocks';

import { getMortgageRatesContent } from './mortgageRatesContent';
import { CONTENT_PAGE } from './mortgageRatesSections';
import { USState } from './mortgageRatesState';

export const STATE_RATES_TEMPLATE_URI = 'mortgage-rates/state-template';
export const STATE_RATES_TEMPLATE_TIMEOUT_MS = 5000;

const isValidBlock = (block: unknown): boolean => {
    if (!block || typeof block !== 'object' || Array.isArray(block)) {
        return false;
    }

    const { blockName, innerBlocks } = block as {
        blockName?: unknown;
        innerBlocks?: unknown;
    };

    return (
        typeof blockName === 'string' &&
        (innerBlocks === undefined ||
            (Array.isArray(innerBlocks) && innerBlocks.every(isValidBlock)))
    );
};

/**
 * Fetch the state rates template (homeloans post 3086) from cms-graph.
 * Falls back to CONTENT_PAGE so a graph outage or unpublished template
 * never blanks the state pages.
 */
export async function getStateRatesTemplate(): Promise<unknown[]> {
    let timer: ReturnType<typeof setTimeout> | undefined;

    try {
        // The cms-blocks Apollo client has no request timeout, and state
        // pages render with fallback: 'blocking', so a stalled graph would
        // hang uncached pages instead of reaching the fallback.
        const { post } = await Promise.race([
            getPost({
                variables: {
                    site: 'ZHL',
                    id: STATE_RATES_TEMPLATE_URI,
                    idType: 'URI',
                },
                fetchPolicy: 'no-cache',
            }),
            new Promise<never>((_, reject) => {
                timer = setTimeout(
                    () =>
                        reject(
                            new Error(
                                `timed out after ${STATE_RATES_TEMPLATE_TIMEOUT_MS}ms`
                            )
                        ),
                    STATE_RATES_TEMPLATE_TIMEOUT_MS
                );
            }),
        ]);

        if (post?.status === 'publish') {
            const blocks = JSON.parse(post.blocks || '[]');
            if (
                Array.isArray(blocks) &&
                blocks.length > 0 &&
                blocks.every(isValidBlock)
            ) {
                return blocks;
            }
        }

        console.warn(
            `State rates template ${STATE_RATES_TEMPLATE_URI} unavailable (status: ${post?.status}), using CONTENT_PAGE fallback`
        );
    } catch (error) {
        console.warn(
            `Error fetching state rates template ${STATE_RATES_TEMPLATE_URI}, using CONTENT_PAGE fallback:`,
            error
        );
    } finally {
        clearTimeout(timer);
    }

    return CONTENT_PAGE;
}

/**
 * Process mortgage rates blocks for a state from the WordPress template
 * @returns Processed blocks ready for BlockList component
 */
export async function processMortgageRatesBlocks(
    stateData: USState
): Promise<unknown[]> {
    try {
        const template = await getStateRatesTemplate();
        const blocksContent = getMortgageRatesContent(template, stateData);
        const processedBlocks = await getBlocksData(blocksContent);

        return processedBlocks;
    } catch (error) {
        console.error('Error processing mortgage rates blocks:', error);
        return [];
    }
}

export async function getStateRatesBlocks(
    stateData: USState
): Promise<unknown[]> {
    return await processMortgageRatesBlocks(stateData);
}
